import { DEFAULT_CALLBACK_SECONDS } from "@/lib/dispositions";
import type { CalendarCallback, Disposition, LeadCallback, LeadCardCall } from "@/lib/types";

/**
 * A callback at the time the rep picked (ADR-080): the lead's wall clock to an instant and back,
 * the time a picker opens on, what is wrong with a time, and the words a callback is said in.
 *
 * The rep picks on the LEAD's clock. "Call me back at three" is the lead's three, and it is the
 * clock the dialer calls them on: `clock_timezone` on the card — their own zone, else their state's,
 * else the calling campaign's. The time goes to core-api as an instant with that zone's offset. With
 * no zone known the picker falls back to the rep's own clock, and says so.
 */

/** How far ahead core-api takes a callback (`callbacks.HORIZON`). */
export const CALLBACK_HORIZON_DAYS = 90;
/** The hours a campaign may call a lead in at the widest, on their clock: 8 AM to 9 PM. */
export const CALLING_HOURS = { from: 8, to: 21 } as const;

const MINUTE = 60_000;
const QUARTER = 15 * MINUTE;
const DAY = 86_400_000;

/** A wall clock: the date and the time a person reads, in no zone of its own. */
export interface WallTime {
  /** YYYY-MM-DD, what `<input type="date">` holds. */
  date: string;
  /** HH:mm (24 h), what `<input type="time">` holds. */
  time: string;
}

/** The browser's own zone: the rep's clock. */
export function ownZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
}

/** A zone this browser knows, or null. */
export function knownZone(zone: string | null | undefined): string | null {
  if (!zone) return null;
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: zone });
    return zone;
  } catch {
    return null;
  }
}

interface Parts {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
}

function parts(instant: number, zone: string): Parts {
  const out: Parts = { year: 0, month: 0, day: 0, hour: 0, minute: 0, second: 0 };
  const fmt = new Intl.DateTimeFormat("en-US", {
    timeZone: zone,
    hourCycle: "h23",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
  for (const p of fmt.formatToParts(instant)) {
    if (!(p.type in out)) continue;
    const n = Number(p.value);
    // Some ICU builds say midnight as hour 24 even with h23.
    out[p.type as keyof Parts] = p.type === "hour" ? n % 24 : n;
  }
  return out;
}

/** How far `zone`'s clock is ahead of UTC at `instant`, in milliseconds (-7 h is negative). */
export function offsetAt(instant: number, zone: string): number {
  const p = parts(instant, zone);
  const asUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
  return asUtc - Math.floor(instant / 1000) * 1000;
}

const pad = (n: number) => String(n).padStart(2, "0");

/** The wall clock `zone` shows at `instant`. */
export function wallTime(instant: number, zone: string): WallTime {
  const p = parts(instant, zone);
  return { date: `${p.year}-${pad(p.month)}-${pad(p.day)}`, time: `${pad(p.hour)}:${pad(p.minute)}` };
}

/** The instant a wall clock names in `zone`; null for a date or time that is not one. A time the
 *  clocks skip in spring, or show twice in autumn, is read as the zone's offset just before it. */
export function instantOf(wall: WallTime, zone: string): number | null {
  const d = /^(\d{4})-(\d{2})-(\d{2})$/.exec(wall.date);
  const t = /^(\d{2}):(\d{2})$/.exec(wall.time);
  if (!d || !t) return null;
  const guess = Date.UTC(Number(d[1]), Number(d[2]) - 1, Number(d[3]), Number(t[1]), Number(t[2]));
  if (Number.isNaN(guess)) return null;
  const first = guess - offsetAt(guess, zone);
  return guess - offsetAt(first, zone);
}

/** RFC 3339 with the zone's own offset, as core-api is sent a callback: "2026-09-30T15:00:00-07:00". */
export function isoWithOffset(instant: number, zone: string): string {
  const w = wallTime(instant, zone);
  const minutes = Math.round(offsetAt(instant, zone) / MINUTE);
  const sign = minutes < 0 ? "-" : "+";
  const abs = Math.abs(minutes);
  return `${w.date}T${w.time}:00${sign}${pad(Math.floor(abs / 60))}:${pad(abs % 60)}`;
}

/** Up to the next quarter hour: a callback is promised for 3:15, not 3:07. */
export function nextQuarter(instant: number): number {
  return Math.ceil(instant / QUARTER) * QUARTER;
}

/** Where a picker opens: the outcome's own wait from now — what a callback given no time does —
 *  up to the next quarter hour. */
export function defaultCallback(outcome: Pick<Disposition, "backoff_seconds">, now: number): number {
  return nextQuarter(now + (outcome.backoff_seconds ?? DEFAULT_CALLBACK_SECONDS) * 1000);
}

/** Up to the next whole minute: "in 5 minutes" said at 2:07:30 is 2:13. */
export function nextMinute(instant: number): number {
  return Math.ceil(instant / MINUTE) * MINUTE;
}

/** A callback a few minutes out ("call me back in ten"): one pick whose minutes the rep chooses, kept
 *  as their own for next time. The minutes offered, where it starts, and the most it takes. */
export const SOON_MINUTES = [5, 10, 15, 20, 30, 45] as const;
export const SOON_DEFAULT = 10;
export const SOON_MAX = 120;
/** The key in the rep's own preferences (`PUT /auth/me/ui-prefs`). */
export const SOON_PREF = "callback_minutes";

/** The minutes saved in the rep's preferences, when they are a whole number the pick takes. */
export function soonMinutes(saved: unknown): number {
  return typeof saved === "number" && Number.isInteger(saved) && saved >= 1 && saved <= SOON_MAX ? saved : SOON_DEFAULT;
}

/** Why minutes typed for the pick cannot be taken, or null. */
export function soonProblem(typed: string): string | null {
  const n = Number(typed.trim());
  return typed.trim() && Number.isInteger(n) && n >= 1 && n <= SOON_MAX ? null : `A whole number of minutes, 1 to ${SOON_MAX}`;
}

/** "In 10 min": to the minute (the lead asked to be called right back), never sooner than asked. */
export function soonPick(now: number, minutes: number): { label: string; instant: number } {
  return { label: `In ${minutes} min`, instant: nextMinute(now + minutes * MINUTE) };
}

/** The times a rep says most, on the lead's clock: in an hour, in three, tomorrow morning and
 *  afternoon. A few minutes out is `soonPick`. */
export function quickPicks(now: number, zone: string): { label: string; instant: number }[] {
  const tomorrow = wallTime(instantOf(wallTime(now, zone), zone)! + DAY, zone).date;
  const at = (time: string) => instantOf({ date: tomorrow, time }, zone)!;
  return [
    { label: "In 1 hour", instant: nextQuarter(now + 60 * MINUTE) },
    { label: "In 3 hours", instant: nextQuarter(now + 180 * MINUTE) },
    { label: "Tomorrow 10 AM", instant: at("10:00") },
    { label: "Tomorrow 2 PM", instant: at("14:00") },
  ];
}

/** What stops a callback at this time, in core-api's words: none picked, one that has passed (a
 *  minute of grace for the clock), one past the horizon. Null when it may be saved. */
export function callbackProblem(instant: number | null, now: number): string | null {
  if (instant === null) return "Pick a date and a time.";
  if (instant < now - MINUTE) return "That time has passed: pick a time from now on.";
  if (instant > now + CALLBACK_HORIZON_DAYS * DAY) return `A callback can be at most ${CALLBACK_HORIZON_DAYS} days ahead.`;
  return null;
}

/** Whether the time falls outside the hours any campaign may call the lead in, on their clock. The
 *  dialer holds the call until the hours open, so this is a warning, not a refusal. */
export function outsideCallingHours(instant: number, zone: string): boolean {
  const { hour } = parts(instant, zone);
  return hour < CALLING_HOURS.from || hour >= CALLING_HOURS.to;
}

/** "PDT", "CST", or "GMT+5" where the zone has no letters. */
export function zoneName(instant: number, zone: string): string {
  const found = new Intl.DateTimeFormat("en-US", { timeZone: zone, timeZoneName: "short" })
    .formatToParts(instant)
    .find((p) => p.type === "timeZoneName");
  return found?.value ?? zone;
}

/** "Tue, Sep 30, 3:00 PM" on `zone`'s clock. */
export function whenText(instant: number | string, zone: string): string {
  const at = typeof instant === "string" ? Date.parse(instant) : instant;
  // Newer ICU puts a narrow no-break space before "PM"; the rest of the console uses a plain one.
  return new Intl.DateTimeFormat("en-US", {
    timeZone: zone,
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  })
    .format(at)
    .replaceAll(" ", " ");
}

/** A callback's time as the record says it: on the lead's clock with its zone, and the reader's
 *  own beside it when the two differ. */
export function callbackWhen(
  dueAt: string | number,
  leadZone: string | null,
  viewerZone: string = ownZone(),
): { there: string; yours: string | null } {
  const at = typeof dueAt === "string" ? Date.parse(dueAt) : dueAt;
  const zone = knownZone(leadZone);
  if (!zone) return { there: `${whenText(at, viewerZone)} your time`, yours: null };
  const there = `${whenText(at, zone)} ${zoneName(at, zone)}`;
  const same = offsetAt(at, zone) === offsetAt(at, viewerZone);
  return { there, yours: same ? null : `${whenText(at, viewerZone)} your time` };
}

/** "in 2 hours", "10 minutes ago". */
function inWords(at: number, now: number): string {
  const seconds = Math.round((at - now) / 1000);
  const rtf = new Intl.RelativeTimeFormat("en-US", { numeric: "auto" });
  const units: [Intl.RelativeTimeFormatUnit, number][] = [["day", 86_400], ["hour", 3_600], ["minute", 60]];
  for (const [unit, per] of units) {
    if (Math.abs(seconds) >= per) return rtf.format(Math.round(seconds / per), unit);
  }
  return "now";
}

export type CallbackTone = "accent" | "warning" | "neutral" | "danger";

/** Where a callback stands, as a badge says it (`callbacks.STATUSES` in core-api). */
export function callbackStatus(
  cb: Pick<LeadCallback, "status" | "due_at">,
  now: number,
): { label: string; tone: CallbackTone; detail: string | null } {
  const due = Date.parse(cb.due_at);
  switch (cb.status) {
    case "upcoming":
      return { label: "Upcoming", tone: "accent", detail: inWords(due, now) };
    case "due":
      return { label: "Due", tone: "warning", detail: now - due < MINUTE ? "now" : inWords(due, now) };
    case "done":
      return { label: "Done", tone: "neutral", detail: null };
    case "rescheduled":
      return { label: "Rescheduled", tone: "neutral", detail: null };
    case "cancelled":
      return { label: "Cancelled", tone: "danger", detail: "the lead has left" };
    case "missed":
      return { label: "Missed", tone: "warning", detail: "nobody answered at the promised time" };
    default:
      return { label: cb.status, tone: "neutral", detail: null };
  }
}

/* ---------------------------------------------------------------------------------------------- */
/* What a callback is for, and who makes its call (ACL-506, ADR-162 in `platform`)                */
/* ---------------------------------------------------------------------------------------------- */

/** What a rep says a callback is for, picked with the time; any other words are typed. */
export const CALLBACK_PURPOSES = ["Contract review", "Signing", "Follow-up"] as const;
/** As long as a purpose may be: a few words, not a note (`PURPOSE_MAX` in core-api). */
export const PURPOSE_MAX = 120;

/** The purpose as sent: its words trimmed and joined, nothing when there are none. */
export function purposeToSend(words: string): string | null {
  const trimmed = words.split(/\s+/).filter(Boolean).join(" ");
  return trimmed ? trimmed.slice(0, PURPOSE_MAX) : null;
}

type Kept = Pick<LeadCallback, "rep" | "rep_user_id" | "dialer">;

/** Whether the callback is kept for somebody other than the reader: theirs to make, not the reader's. */
export function keptForSomebodyElse(cb: Pick<LeadCallback, "rep_user_id"> | null | undefined, viewerId: number | null | undefined): boolean {
  return cb?.rep_user_id != null && viewerId != null && cb.rep_user_id !== viewerId;
}

/**
 * Who places the callback's call, in a line, so nobody dials the lead by hand while the dialer is
 * about to: the dialer calls and connects the rep, offers the lead to the rep, or nothing does and
 * the rep calls. Nothing from an older core-api, which does not say.
 */
export function whoCalls(cb: Kept, viewerId: number | null | undefined): string | null {
  if (cb.dialer === undefined) return null;
  const mine = cb.rep_user_id != null && cb.rep_user_id === viewerId;
  const rep = cb.rep ?? "its rep";
  if (cb.dialer === "power") {
    return mine
      ? "The dialer calls them and connects you: no need to dial."
      : `The dialer calls them and connects ${rep}: don't dial them yourself.`;
  }
  if (cb.dialer === "preview") {
    return mine ? "The dialer offers them to you to dial." : `The dialer offers them to ${rep} to dial.`;
  }
  return mine ? "No dialing list will call them: call them yourself." : `No dialing list will call them: ${rep} calls.`;
}

/**
 * Why a rep should think before dialing the lead by hand: the dialer is about to call them for the
 * rep the callback is kept for. Null when it is the reader's own, when nothing will dial it, or
 * when there is no callback still to make.
 */
export function manualDialWarning(
  cb: (Kept & Pick<LeadCallback, "status" | "due_at" | "purpose">) | null | undefined,
  viewerId: number | null | undefined,
  leadZone: string | null,
): string | null {
  if (!cb || (cb.status !== "upcoming" && cb.status !== "due") || !cb.dialer) return null;
  if (!keptForSomebodyElse(cb, viewerId)) return null;
  const rep = cb.rep ?? "another rep";
  const what = cb.purpose ? ` (${cb.purpose})` : "";
  return cb.status === "due"
    ? `The dialer is calling them back for ${rep}${what} now. Call them anyway?`
    : `The dialer will call them back for ${rep}${what} at ${callbackWhen(cb.due_at, leadZone).there}. Call them anyway?`;
}

/**
 * The thread's words for a call the dialer made for a callback: "Callback call · Connected with
 * Ana · Signing", "Callback call · No response", and whose it was when somebody else took it.
 * Null for any other call.
 */
export function callbackCallText(
  call: Pick<LeadCardCall, "callback" | "via" | "talk_seconds" | "outcome" | "rep_name" | "rep">,
): string | null {
  const cb = call.callback;
  if (!cb) return null;
  const connected = call.via === "queue" && (call.talk_seconds ?? 0) > 0;
  const who = call.rep_name ?? call.rep;
  const parts = ["Callback call"];
  if (connected) parts.push(who ? `Connected with ${who}` : "Connected");
  else parts.push(call.outcome === "voicemail" ? "Voicemail" : "No response");
  if (cb.rep && who && cb.rep !== who) parts.push(`originally ${cb.rep}'s`);
  if (cb.purpose) parts.push(cb.purpose);
  return parts.join(" · ");
}

/* ---------------------------------------------------------------------------------------------- */
/* Backup times: "4 pm, or else 7 pm, then 10 am" (ACL-559, ADR-183 and ADR-210 in `platform`)     */
/* ---------------------------------------------------------------------------------------------- */

/**
 * A callback whose time was picked may carry up to four backup times, for when the dialer's call at
 * the time before reaches nobody. Core-api keeps them on the callback and tells the dialer nothing of
 * them: only once a call is recorded as missed does it book the next, as a callback of its own that
 * keeps the times after it. So a backup is said as what happens "if nobody answers", and where it
 * stands comes from core-api (`backup_state`): the console cannot see whether the dialer still holds
 * the first call.
 */

/** How long after the callback's own time a backup may be at the soonest when the cadence works a
 *  callback for no window: the call and its redials end well inside it (`callbacks.BACKUP_GAP` in
 *  core-api). With a window (ACL-661) the gap is the window: `backupGapMinutes`. */
export const BACKUP_GAP_MINUTES = 15;

/** The gap a backup keeps from the time before it: the callback's window when the cadence has one
 *  (`callback_window_minutes` on the lead's card, ACL-661), else `BACKUP_GAP_MINUTES`. */
export function backupGapMinutes(windowMinutes: number | null | undefined): number {
  return windowMinutes ?? BACKUP_GAP_MINUTES;
}

/** "15 minutes", "1 hour", "1 hour 30 minutes", "2 hours": a gap as the rule says it. */
export function gapWords(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  const h = hours === 0 ? "" : hours === 1 ? "1 hour" : `${hours} hours`;
  const m = rest === 0 ? "" : rest === 1 ? "1 minute" : `${rest} minutes`;
  return [h, m].filter(Boolean).join(" ") || "0 minutes";
}

/** The end of a callback's window: its time plus the cadence's window (ACL-661). */
export function workedUntil(due: number, windowMinutes: number): number {
  return due + windowMinutes * MINUTE;
}

/** "5:00 PM", or "Fri 10:00 AM" when the window ends on another day than the callback's time, on
 *  the lead's clock when it is known, else the viewer's: the clock `callbackWhen` says the time on. */
export function untilClock(until: string | number, due: string | number, leadZone: string | null, viewerZone: string = ownZone()): string {
  const zone = knownZone(leadZone) ?? viewerZone;
  const end = typeof until === "number" ? until : Date.parse(until);
  const at = typeof due === "number" ? due : Date.parse(due);
  return clockText(end, zone, wallTime(at, zone).date !== wallTime(end, zone).date);
}

/** How many backup times a callback may carry: five calls in all (`callbacks.MAX_BACKUPS`). */
export const MAX_BACKUPS = 4;

/** Where a backup picker opens: three hours after the callback, up to the next quarter hour, and
 *  no further ahead than core-api takes a callback. */
export function defaultBackup(firstInstant: number, now: number = Date.now()): number {
  return Math.min(nextQuarter(firstInstant + 180 * MINUTE), now + CALLBACK_HORIZON_DAYS * DAY);
}

/** What stops a backup at this time, in core-api's words and its order: none picked, one that has
 *  passed, one too close to the time before it (the callback's own, or for a later backup the one
 *  before it: `later`), one past the horizon. Null when it may be saved. */
export function backupProblem(
  instant: number | null,
  firstInstant: number | null,
  now: number,
  later = false,
  /** The gap from the time before it: the callback's window when the cadence has one (ACL-661). */
  gapMinutes: number = BACKUP_GAP_MINUTES,
): string | null {
  if (instant === null) return "Pick a date and a time.";
  if (instant < now - MINUTE) return "That time has passed: pick a time from now on.";
  if (firstInstant !== null && instant < firstInstant + gapMinutes * MINUTE) {
    return later
      ? `Pick a time at least ${gapWords(gapMinutes)} after the backup before it.`
      : `Pick a time at least ${gapWords(gapMinutes)} after the callback's time.`;
  }
  if (instant > now + CALLBACK_HORIZON_DAYS * DAY) return `A callback can be at most ${CALLBACK_HORIZON_DAYS} days ahead.`;
  return null;
}

/** "7:00 PM", or "Thu 7:00 PM" with the day, on `zone`'s clock (with a plain space, as `whenText`). */
export function clockText(instant: number, zone: string, withDay: boolean): string {
  return new Intl.DateTimeFormat("en-US", {
    timeZone: zone,
    ...(withDay ? { weekday: "short" as const } : {}),
    hour: "numeric",
    minute: "2-digit",
  })
    .format(instant)
    .replaceAll(" ", " ");
}

/** A callback's backup times in order: `backup_times`, else its one `backup_at` from a core-api that
 *  keeps one (ADR-183), else none. */
export function backupTimesOf(cb: Partial<Pick<LeadCallback, "backup_at" | "backup_times">>): string[] {
  if (cb.backup_times && cb.backup_times.length > 0) return cb.backup_times;
  return cb.backup_at ? [cb.backup_at] : [];
}

/** "Fri 7:00 PM PDT, then Sat 10:00 AM": backup times in a row, the first with its zone, each with
 *  its day when that is not the day of the time before it. */
export function backupTimesText(times: readonly (string | number)[], zone: string, known: boolean, after: number): string {
  let before = after;
  return times
    .map((time, i) => {
      const at = typeof time === "number" ? time : Date.parse(time);
      const clock = clockText(at, zone, wallTime(before, zone).date !== wallTime(at, zone).date || i === 0);
      before = at;
      return i === 0 ? `${clock} ${known ? zoneName(at, zone) : "your time"}` : clock;
    })
    .join(", then ");
}

/** Backup times as a toast or a hint says them: one as `callbackWhen` does, several in a row
 *  ("Fri 7:00 PM PDT, then Sat 10:00 AM") counted from the callback's own time `after`. */
export function backupTimesWhen(
  times: readonly (string | number)[],
  leadZone: string | null,
  after: number,
  viewerZone: string = ownZone(),
): string {
  if (times.length === 1) return callbackWhen(times[0]!, leadZone, viewerZone).there;
  const known = knownZone(leadZone);
  return backupTimesText(times, known ?? viewerZone, known !== null, after);
}

/** The backup times an outcome's answer says were kept: its list, else its one `callback_backup_at`. */
export function keptBackups(result: { callback_backup_at?: string | null; callback_backup_times?: string[] }): string[] {
  if (result.callback_backup_times && result.callback_backup_times.length > 0) return result.callback_backup_times;
  return result.callback_backup_at ? [result.callback_backup_at] : [];
}

/** What a core-api kept of the backup times an outcome sent it, from its answer's echo: all of them,
 *  the first alone (one that keeps one backup, ADR-183 in `platform`, and drops the list), or none
 *  (one that keeps no backup). */
export function backupsKept(
  sent: readonly string[],
  result: { callback_backup_at?: string | null; callback_backup_times?: string[] },
): "all" | "first" | "none" {
  if (sent.length === 0) return "all";
  if (!("callback_backup_at" in result)) return "none";
  if (sent.length > 1 && !("callback_backup_times" in result)) return "first";
  return "all";
}

/**
 * A callback's backup times in a line, beside the callback's own: what happens if nobody answers
 * while they wait, why they will not be called when nothing will make the first call, that the next
 * was booked or they went unused. On the callback the system booked from a backup, that it is the
 * backup, and the times still after it. Null for a callback with neither, and from an older
 * core-api, which does not say.
 */
export function backupLine(
  cb: Pick<LeadCallback, "due_at"> & Partial<Pick<LeadCallback, "backup_at" | "backup_times" | "backup_state" | "backup_of">>,
  leadZone: string | null,
  viewerZone: string = ownZone(),
): { text: string; tone: CallbackTone } | null {
  const known = knownZone(leadZone);
  const zone = known ?? viewerZone;
  const times = backupTimesOf(cb);
  const first = Date.parse(cb.due_at);
  if (cb.backup_of) {
    const missed = clockText(Date.parse(cb.backup_of.due_at), zone, true);
    const next = times.length > 0 && cb.backup_state === "waiting"
      ? ` · or else ${backupTimesText(times, zone, known !== null, first)} if nobody answers`
      : "";
    return { text: `The backup time: nobody answered at ${missed}${next}`, tone: "accent" };
  }
  if (times.length === 0 || !cb.backup_state) return null;
  // The callback's own time is on the same row: its clock is enough. A backup's day is said when
  // it is another day's.
  const firstAt = clockText(first, zone, false);
  const clocks = times.map((time) => {
    const at = Date.parse(time);
    return clockText(at, zone, wallTime(first, zone).date !== wallTime(at, zone).date);
  });
  const backupAt = clocks.join(", ");
  const several = times.length > 1;
  switch (cb.backup_state) {
    case "waiting":
      return {
        text: `Or else ${backupTimesText(times, zone, known !== null, first)} if nobody answers at ${firstAt}`,
        tone: "accent",
      };
    case "off":
      return {
        text: `${several ? "Backups" : "Backup"} ${backupAt} won't be called: no dialing list will make the ${firstAt} call`,
        tone: "warning",
      };
    case "booked":
      return { text: `Backup ${clocks[0]} booked: nobody answered at ${firstAt}`, tone: "neutral" };
    case "unused":
      return { text: `${several ? "Backups" : "Backup"} ${backupAt} not used`, tone: "neutral" };
    default:
      return null;
  }
}

/**
 * Whether a backup time may be added to the callback, or changed: one whose time was picked, still to
 * be made, whose call the dialer will make (with nothing to make it, nothing will miss it), and not
 * itself the backup of another. Never from an older core-api, which keeps no backup time.
 */
export function canChangeBackup(
  cb: Pick<LeadCallback, "picked" | "status"> & Partial<Pick<LeadCallback, "dialer" | "backup_state" | "backup_of">>,
): boolean {
  return (
    "backup_state" in cb &&
    cb.picked &&
    !cb.backup_of &&
    (cb.status === "upcoming" || cb.status === "due") &&
    !!cb.dialer
  );
}

/**
 * Who will take a callback's call, said before it is booked (ACL-657, ADR-216 in `platform`). The
 * dialer decides, by the stage the outcome moves the lead to: nobody picks a person.
 */
export function whoTakesIt(
  taker: { only_skills: readonly string[]; booker_takes: boolean; kept_minutes?: number } | undefined,
  isRep: boolean,
): string | null {
  if (!taker) return null;
  const who = taker.only_skills.join(" and ").toLowerCase();
  const closed = taker.only_skills.length > 0;
  const wait = taker.kept_minutes === undefined ? "" : ` ${waitWords(taker.kept_minutes)}`;
  if (!isRep) {
    return closed
      ? `The next free ${who} takes this call at that time.`
      : `It goes to the rep who last spoke to them. If they are not free, it waits${wait}, then the next free rep takes it.`;
  }
  if (closed && !taker.booker_takes) {
    return `You are not a ${who}, so the next free ${who} takes this call at that time.`;
  }
  return closed
    ? `You are a ${who}, so this comes back to you. If you are not free, it waits${wait} for you, then the next free ${who} takes it.`
    : `This comes back to you. If you are not free at that time, it waits${wait} for you, then the next free rep takes it.`;
}

function waitWords(minutes: number): string {
  if (minutes === 1) return "1 minute";
  if (minutes === 60) return "an hour";
  return minutes % 60 === 0 ? `${minutes / 60} hours` : `${minutes} minutes`;
}

/** The form's Next line for a time and its backups, on the lead's clock: "called back Fri 4:00 PM
 *  PDT; if nobody answers, again at 7:00 PM". Who takes it is the form's own line (`whoTakesIt`). */
export function callbackNext(instant: number, lead: string | null, backups: readonly number[]): string {
  const when = `called back ${callbackWhen(instant, lead).there}`;
  return backups.length > 0 ? `${when}; if nobody answers, again at ${backupTimesWhen(backups, lead, instant)}` : when;
}

/** What a callback booked with this outcome is for, on its reminder (ADR-162): the outcome's own
 *  name, unless that says no more than "Callback". */
export function purposeOf(label: string): string | null {
  return /^call ?back$/i.test(label.trim()) ? null : label.trim();
}

/* ---------------------------------------------------------------------------------------------- */
/* Callbacks on the calendar (ACL-658, ADR-217 in `platform`)                                     */
/* ---------------------------------------------------------------------------------------------- */

/** How long a callback is drawn, and how long it keeps a rep busy for an appointment
 *  (`booking.CALLBACK_BUSY`). */
export const CALLBACK_MINUTES = 30;

/** Each thing to draw for a callback: its own time, and each backup while its call could still be
 *  missed. A list that will not dial cannot miss it, and a backup already booked is a callback of its
 *  own. `backup` is 1 for the first backup time, 0 for the callback itself. */
export function calendarTimes(cb: CalendarCallback): { at: number; backup: number }[] {
  const own = { at: Date.parse(cb.due_at), backup: 0 };
  if (cb.backup_state !== "waiting") return [own];
  return [own, ...cb.backup_times.map((time, i) => ({ at: Date.parse(time), backup: i + 1 }))];
}

/** Which of the calendar's looks a callback has: going to a rep with a skill, or back to its rep. */
export function forSkilled(cb: Pick<CalendarCallback, "only_skills" | "rep">): boolean {
  return cb.only_skills.length > 0 && cb.rep === null;
}

/** Who takes a callback on the calendar, as its details say it: the rep it is kept for, or the next
 *  free one with the skill. */
export function whoTakesCalendarCallback(cb: Pick<CalendarCallback, "only_skills" | "rep">): string {
  if (cb.rep) return cb.rep;
  return cb.only_skills.length > 0
    ? `The next free ${cb.only_skills.join(" and ").toLowerCase()}`
    : "The next free rep";
}

/** How a callback's call will be made, in words. */
export function howItIsCalled(dialer: CalendarCallback["dialer"]): string {
  if (dialer === "power") return "The dialer calls and connects the rep";
  if (dialer === "preview") return "The dialer offers the lead to the rep";
  return "No list will dial it, so the rep calls";
}

/**
 * What a rep already has at a callback's time, to warn of before saving (ADR-217): an appointment, or
 * another lead's callback kept for them. Only a warning: an outcome must always save during wrap-up.
 * Both are read on `zone`, the clock the time is being picked on.
 */
export function callbackClash(
  instant: number,
  zone: string,
  appointments: readonly { starts_at: string; ends_at: string; title: string; contact_name?: string | null; status: string }[],
  callbacks: readonly Pick<CalendarCallback, "due_at" | "lead_id" | "rep_user_id" | "status" | "contact_name">[],
  me: number,
  leadId: string,
): string | null {
  const end = instant + CALLBACK_MINUTES * MINUTE;
  const meeting = appointments.find(
    (a) =>
      a.status !== "cancelled" && a.status !== "rescheduled" &&
      Date.parse(a.starts_at) < end && instant < Date.parse(a.ends_at),
  );
  if (meeting) {
    const who = meeting.contact_name ? ` with ${meeting.contact_name}` : "";
    return `You have an appointment at ${clockText(Date.parse(meeting.starts_at), zone, true)}${who}. You can still book this time.`;
  }
  const other = callbacks.find(
    (cb) =>
      cb.rep_user_id === me && cb.lead_id !== leadId &&
      (cb.status === "upcoming" || cb.status === "due") &&
      Math.abs(Date.parse(cb.due_at) - instant) < CALLBACK_MINUTES * MINUTE,
  );
  if (other) {
    const who = other.contact_name ? ` with ${other.contact_name}` : "";
    return `You have a callback at ${clockText(Date.parse(other.due_at), zone, true)}${who}. You can still book this time.`;
  }
  return null;
}
